import { Breadcrumb, Button, MenuProps } from 'antd';
import './PageScaffold.css';
import { Link, useNavigate } from 'react-router-dom';
import PageScaffoldControls from './page_scaffold/PageScaffoldControls';
import { useContext, useEffect } from 'react';
import UIContext from '../contexts/ui/UIContext';
import PageScaffoldContext from '../contexts/ui/PageScaffoldContext';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import CompanySubscriptionsStatusAlert from './@shared/CompanySubscriptionsStatusAlert/CompanySubscriptionsStatusAlert';

export type PageScaffoldButtonType = 'primary' | 'default' | 'dashed' | 'link' | 'text';

interface PageScaffoldProps {
  title: string;
  subtitle?: string;
  noPadding?: boolean;
  hideHeader?: boolean;
  /** Rendered above the page head, kept even with hideHeader (e.g. the "Mon entreprise" tabs) */
  header?: React.ReactNode;
  breadcrumbItems?: {
    target?: string;
    title: React.ReactNode;
  }[];
  controlsSettings?: {
    countryAddonBefore?: boolean;
    searchLabel: string;
    onSearch: (input: string, addonBeforeQuery: string) => void;
    onSearchCancel: () => void;
    hideSearch?: boolean;
    isLoading: boolean;
    searchInputs?: (inputs: React.ReactNode) => React.ReactNode;
    actionButtons: {
      label: string | React.ReactNode;
      type?: PageScaffoldButtonType;
      props?: React.ComponentProps<typeof Button>;
      buttonType?: string;
      items?: MenuProps['items'] | undefined;
    }[];
  };
  filterSettings?: {
    placeholder?: string;
    clearable?: boolean;
    onFilter: (filter: string) => void;
    values: {
      label: string;
      value: string;
    }[];
    selectedValue: string | undefined | null;
  };
  filters?: React.ReactNode;
}

const PageScaffold: React.FC<React.PropsWithChildren<PageScaffoldProps>> = ({
  children,
  title,
  subtitle,
  noPadding,
  hideHeader,
  header,
  breadcrumbItems,
  controlsSettings,
  filterSettings,
  filters,
}) => {
  const uiContext = useContext(UIContext);
  const pageScaffoldContext = useContext(PageScaffoldContext);
  const contentElement = pageScaffoldContext?.contentElement;
  const navigate = useNavigate();
  const windowSize = useWindowSize();

  useEffect(() => {
    uiContext.setPageTitle(title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  useEffect(() => {
    if (!contentElement) return;

    if (noPadding) {
      contentElement.style.padding = '0';
    } else if (windowSize.width < 1024 && windowSize.width > 0) {
      contentElement.style.padding = '24px';
    }
  }, [windowSize.width, noPadding, contentElement]);

  // We parse the breadcrumb items and add use onClick to navigate to the target
  const breadcrumbItemsWithNavigation = breadcrumbItems?.map((item) => {
    if (!item.target) return item;
    return {
      ...item,
      onClick: () => {
        if (item.target) {
          navigate(item.target);
        }
      },
    };
  });

  return (
    <div className="page-scaffold">
      <div ref={pageScaffoldContext?.setContentElement} className="page-scaffold__content">
        <CompanySubscriptionsStatusAlert />
        {header}
        {!hideHeader && (
          <div className="page-scaffold__page-head">
            {breadcrumbItems && (
              <div className="page-scaffold__page-head__breadcrumb">
                <Breadcrumb
                  separator=">"
                  items={breadcrumbItemsWithNavigation}
                  itemRender={(currentRoute) => {
                    if (currentRoute.onClick) {
                      const originalTarget =
                        breadcrumbItems?.find((item) => item.title === currentRoute.title)
                          ?.target || '';
                      return <Link to={originalTarget}>{currentRoute.title}</Link>;
                    }
                    return currentRoute.title;
                  }}
                />
              </div>
            )}
            <h1>{title}</h1>
            {subtitle && <h3>{subtitle}</h3>}
          </div>
        )}

        {controlsSettings && (
          <PageScaffoldControls
            countryAddonBefore={controlsSettings.countryAddonBefore}
            searchLabel={controlsSettings.searchLabel}
            onSearch={controlsSettings.onSearch}
            hideSearch={controlsSettings.hideSearch}
            isLoading={controlsSettings.isLoading}
            onSearchCancel={controlsSettings.onSearchCancel}
            searchInputs={controlsSettings.searchInputs}
            actionButtons={controlsSettings.actionButtons}
            filterSettings={filterSettings}
          />
        )}
        {children}
      </div>
    </div>
  );
};

export default PageScaffold;
